import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";

// Presentational cyberpunk nav for the desktop sidebar. Items are already
// scope-filtered + doctor-relabeled by AppSidebar; here we only render rows and
// apply active styling from the current route. `onNavigate` lets the mobile
// drawer close itself when a link is tapped.
type NavItem = {
  to?: string;
  name?: React.ReactNode;
  icon?: React.ReactNode;
  items?: NavItem[];
  [k: string]: unknown;
};

interface AppSidebarNavProps {
  items: NavItem[];
  onNavigate?: () => void;
}

export const AppSidebarNav = ({ items, onNavigate }: AppSidebarNavProps) => {
  const router = useRouter();

  const isActive = (to: string) =>
    to === "/" ? router.pathname === "/" : router.pathname.startsWith(to);

  // Text-color utilities carry `!` (→ !important): Bootstrap's UNLAYERED
  // `a { color }` otherwise wins over our layered tw: color utilities and forces
  // every nav link to near-black. bg/border are safe (Bootstrap doesn't set them
  // on anchors). `indented` insets child rows under a group header.
  const renderLink = (item: NavItem, key: React.Key, indented = false) => {
    const to = (item.to as string) ?? "#";
    const active = isActive(to);
    return (
      <Link
        key={key}
        href={to}
        onClick={onNavigate}
        className={
          // Left-aligned for a consistent icon column across the whole nav;
          // child rows inset under their group header (the only intentional
          // indentation). Icon + text sizes are uniform so top-level items and
          // group children line up identically.
          "tw:relative tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:rounded-xl tw:no-underline " +
          (indented ? "tw:pl-9 " : "") +
          (active
            ? "tw:bg-accent! tw:text-white!"
            : "tw:text-text-muted! tw:hover:text-text! tw:hover:glass-hover")
        }
      >
        {item.icon != null && (
          <span className="tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:items-center tw:justify-center">
            {item.icon as React.ReactNode}
          </span>
        )}
        <span className="tw:text-sm tw:font-medium tw:tracking-tight">{item.name as React.ReactNode}</span>
      </Link>
    );
  };

  return (
    <nav className="tw:flex-1 tw:overflow-y-auto tw:p-3 tw:space-y-0.5 tw:text-sm">
      {items.map((item, index) => {
        // A group (e.g. Inventario) has no `to` of its own. Render it as a quiet
        // uppercase SECTION HEADER (not a row that looks clickable): smaller,
        // muted, dimmed icon, non-interactive cursor. Its children are the
        // actual links, inset beneath it.
        if (item.items?.length) {
          return (
            <div key={`group-${index}`} className="tw:pt-3">
              <div className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:pb-1 tw:text-text-muted tw:select-none tw:cursor-default">
                <span className="tw:flex tw:h-5 tw:w-5 tw:shrink-0 tw:items-center tw:justify-center tw:opacity-60">
                  {item.icon as React.ReactNode}
                </span>
                <span className="tw:text-xs tw:font-semibold tw:uppercase tw:tracking-wider">
                  {item.name as React.ReactNode}
                </span>
              </div>
              <div className="tw:space-y-0.5">
                {item.items.map((child, ci) =>
                  renderLink(child, `${index}-${ci}`, true),
                )}
              </div>
            </div>
          );
        }
        return renderLink(item, item.to || index);
      })}
    </nav>
  );
};

export default AppSidebarNav;
